import React, {useEffect, useState} from 'react';
import DoorLayout from "../components/Layouts/DoorLayout";
import {useLocation} from "react-router-dom";
import useTiming from "../hooks/useTiming";
import useLogin from "../hooks/forms/useLogin";
import {useFormatValidationErrors} from "../hooks/response";
import UpdateProfile from "../components/UpdateProfile";
import queryClient from "../services/queryClient";
import {useRecoilState} from "recoil";
import loggedOutState from "../state/loggedOutState";
import { useTranslation } from 'react-i18next';
import config from '../constants/config';
import SEOHelmet from '../components/SEOHelmet';
import {useFormatDate} from "../hooks/dates";
import FindCodeModal from '../components/Modal/FindCodeModal';
import activeModalState from '../state/ui/activeModalState';
import useFetchTheatron from "../hooks/useFetchTheatron";

const Guest = () => {
  const theatron = useFetchTheatron();
  const timing = useTiming();
  const {formatDate, formatTime} = useFormatDate()
  const startsAtDate = formatDate(timing?.starts_at);
  const startsAtTime = formatTime(timing?.starts_at);
  const {formik, mutation} = useLogin();

  const {t} = useTranslation(['guest', 'global']);

  const valid = queryClient.getQueryData(['valid', timing?.id]);
  const [loggedOut, setLoggedOut] = useRecoilState(loggedOutState);

  const errors = useFormatValidationErrors(mutation.error || {}, formik.errors);
  const {search} = useLocation();

  const [openTicket, setOpenTicket] = useState(false);
  const [activeModal, setShowModal] = useRecoilState(activeModalState);

  useEffect(() => {
    const code = (new URLSearchParams(search)).get('code') || '';
    if (code && code.length === 5) {
      formik.setFieldValue('code', code);
      setOpenTicket(true);
    }
  }, []);

  return (
    <>
      <SEOHelmet title={timing?.event?.title} />
      <DoorLayout>
        {formik.values.redeem ? (
          <div className="door-content">
            <div className="mb-6 mt-2 text-center">
              <h1 className="font-bold text-2xl tracking-tight leading-tight">{t('complete-profile')}</h1>
            </div>
            <UpdateProfile
              type="guest"
              avatar={true}
              name={true}
              email={true}
              phone={true}
              company={timing?.fields?.company}
              companyLabel={timing?.fields?.company_label}
              location={timing?.fields?.location}
              locationLabel={timing?.fields?.location_label}
              remarks={timing?.fields?.remarks}
              remarksHelp={timing?.fields?.remarks_help}
              remarksLabel={timing?.fields?.remarks_label}
              formik={formik}
              errors={errors}
            />
            <div>
              <button type="submit" onClick={formik.handleSubmit} className="btn btn-block btn-yellow">{t('global:enter')}</button>
            </div>
          </div>
        ) : (
          <div className="door-content">
            <div className="mb-6 mt-2 text-center">
              <h1 className="font-bold text-2xl tracking-tight leading-tight">{t('welcome', {app: theatron.data?.name})}</h1>
              <p className="text-gray-900">{t('have-pass-code')}</p>
            </div>
            <div className="mb-4 ticket">
              {timing?.expires_at && !timing?.valid && (
                <div className="p-4 bg-red-200 rounded mb-4"><i className="fas fa-fw fa-info-circle"></i> Your passcode has expired.</div>
              )}
              <div className="flex flex-row flex-wrap">
                <div className="ticket-content">
                  <div className="ticket-background left"></div>
                  <div className="flex flex-row flex-wrap">
                    {timing?.event?.cover && (
                      <div className="ticket-cover">
                        <div className="p-3 md:pl-4 md:py-4 md:pr-3">
                          <div className="ticket-cover-image">
                            <img src={timing?.event?.cover} className="w-full h-full object-cover"/>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="ticket-detail">
                      <div className="ticket-title">
                        <p className="uppercase text-2xs text-opacity-60 tracking-wider font-bold text-gray-500 md:text-white md:text-opacity-75 text-center md:text-left">{t('global:show')}</p>
                        <h2 className="font-bold text-xl md:text-white text-center md:text-left">{timing?.event?.title}</h2>
                        {/* <p className="text-gray-900">{startsAt}</p> */}
                      </div>
                      <div className="ticket-datetime">
                        {
                          timing.venue.type == 'vod' ?
                            <p className="line-clamp-3 text-sm text-gray-900 mb-2 md:mr-3">
                              {timing?.event?.description}
                            </p>
                          :
                            <div className="flex flex-row py-2 border-t flex-1">
                              <div className="pr-3 mr-3 border-r">
                                <p className="uppercase text-2xs text-opacity-60 tracking-wider font-bold text-gray-500">{t('global:date')}</p>
                                <h4 className="font-bold text-base">{startsAtDate}</h4>
                              </div>
                              <div className="">
                                <p className="uppercase text-2xs text-opacity-60 tracking-wider font-bold text-gray-500">{t('global:time')}</p>
                                <h4 className="font-bold text-base">{startsAtTime}</h4>
                              </div>
                            </div> 
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`ticket-code ${openTicket ? 'block':'hidden'}`}>
                  <div className="ticket-background right"></div>
                  <div className="relative h-full">
                    <div className="z-10 px-4 pt-4 md:pt-8 pb-4 h-full flex flex-col justify-between">
                      <div className="mb-3 md:mb-0">
                        <p className="text-gray-500 md:text-white uppercase tracking-wider text-2xs text-center">
                          {t('enter-your-pass-code')}
                        </p>
                        <input
                          type="text"
                          className={`
                            bg-gray-200 md:bg-white block w-1/2 md:w-full m-auto px-4 py-2 mt-2
                            text-center font-bold md:shadow-lg rounded-lg 
                            focus:border-yellow-600 focus:outline-none border
                            ${formik.touched.code && errors.code ? 'border-red-500':' border-gray-200 md:border-white'}
                          `}
                          autoComplete="off"
                          minLength={5}
                          maxLength={5}
                          required
                          {...formik.getFieldProps('code')}
                          disabled={formik.isSubmitting || formik.values.redeem}
                          onChange={e => {
                            formik.setFieldValue('code', e.target.value.toUpperCase());
                          }}
                        />
                        {!formik.touched.code && !errors.code ? null :
                          <p className="text-red-500 uppercase tracking-wider text-2xs text-center mt-4 font-semibold">{errors.code}</p>
                        }
                      </div>
                      <button
                        onClick={()=>setShowModal('findcode')}
                        className="text-blue-400 text-xs text-center font-bold underline tracking-tide leading-tight">
                          {t('where-to-find-passcode')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <button type="submit" onClick={e => {
                if((loggedOut && valid) || openTicket || timing.status === 'public') {
                  formik.handleSubmit(e);
                }
                else {
                  setOpenTicket(true)
                }
              }} className="btn btn-block btn-yellow">{openTicket ? t('global:confirm') : t('global:enter')}</button>
            </div>
          </div>
        )}
        <FindCodeModal/>
      </DoorLayout>
    </>
  )
}

export default Guest;
